import { computed, onMounted, ref, watch } from 'vue'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useCountryPrefixes } from '~/composables/useCountryPrefixes'
import { phoneConfig } from '~/config/phoneConfig'
import type { CountryPicker } from '~/composables/CountryPicker'
import type { CountryPickerItem } from '~/composables/CountryPickerItem'
import type { CountryPickerOptions } from '~/composables/CountryPickerOptions'
import type { CountryPrefix } from '~/services/api/phone/CountryPrefix'

const defaultCountryCode = phoneConfig.allowedCountries[0]

const findCountry = (list: CountryPrefix[], code: string): CountryPrefix | undefined =>
  list.find((c) => c.code_2 === code)

const buttonLabelFor = (country: CountryPrefix, display: string): string => {
  switch (display) {
    case 'prefix':
    case 'flagPrefix':
      return `+${country.prefix}`
    case 'code':
    case 'flagCode':
      return country.code_2
    case 'codePrefix':
      return `${country.code_2} +${country.prefix}`
    default:
      return ''
  }
}

/** Country selection state and Nuxt UI SelectMenu props for the phone country picker. */
export function useCountryPicker(options: CountryPickerOptions): CountryPicker {
  const { countries, loading, load, defaultCountries } = useCountryPrefixes()
  // Loading starts after hydration so the server and client first renders share one synchronous fallback.
  onMounted(() => {
    void load()
  })

  const searchQuery = ref('')
  const erroredFlags = ref(new Set<string>())

  const onCountryFlagError = (event: Event): void => {
    if (!(event.currentTarget instanceof HTMLImageElement)) return
    const flagUrl = event.currentTarget.dataset['countryFlag']
    if (flagUrl !== undefined) erroredFlags.value.add(flagUrl)
  }

  const buttonWidthClass = computed(() =>
    ['flagPrefix', 'flagCode', 'codePrefix'].includes(options.buttonDisplay.value)
      ? 'w-auto px-2'
      : 'w-12'
  )
  const showFlagInButton = computed(() =>
    ['flag', 'flagPrefix', 'flagCode'].includes(options.buttonDisplay.value)
  )

  // Seeded synchronously so the button never renders empty while the cache resolves.
  const selectedCountry = ref<CountryPrefix | null>(
    findCountry(countries.value, defaultCountryCode) ??
      findCountry(defaultCountries, defaultCountryCode) ??
      null
  )

  const buttonLabel = computed(() =>
    selectedCountry.value === null
      ? ''
      : buttonLabelFor(selectedCountry.value, options.buttonDisplay.value)
  )

  const optionLabel = (country: CountryPrefix): string =>
    options.variant.value === 'code' ? country.code : `+${country.prefix} ${country.name}`

  watch(countries, (list) => {
    if (list.length === 0) return
    const current = selectedCountry.value
    if (current === null || current.code_2 === defaultCountryCode) {
      selectedCountry.value = findCountry(list, defaultCountryCode) ?? current
    }
  })

  const filteredCountries = computed(() => {
    const query = searchQuery.value.trim().toLowerCase()
    const allowedCountries = new Set<string>(phoneConfig.allowedCountries)
    const available = countries.value.filter(
      (c) => allowedCountries.has(c.code_2) && (options.variant.value !== 'code' || Boolean(c.code))
    )
    if (!query) return available
    return available.filter((c) =>
      [c.name, c.code, c.code_2, c.prefix].some((field) => field.toLowerCase().includes(query))
    )
  })

  const selectedCode = computed(() => selectedCountry.value?.code_2 ?? '')

  const countryItems = computed<CountryPickerItem[]>(() =>
    filteredCountries.value.map((country) => ({
      country,
      label: optionLabel(country),
      value: country.code_2
    }))
  )

  const isSelectedCountry = (country: CountryPrefix): boolean =>
    country.code_2 === selectedCode.value

  const onCountrySelect = (code: string): void => {
    const country = findCountry(countries.value, code)
    if (country !== undefined) selectedCountry.value = country
  }

  watch(selectedCountry, () => {
    searchQuery.value = ''
  })

  const t = usePresentationTranslator()
  const searchInputProps = computed(() => ({
    placeholder: t('common.phone.searchCountryOrPrefix')
  }))
  const contentProps = computed(() => ({
    align: 'start' as const,
    ...(options.container.value === null ? {} : { reference: options.container.value })
  }))
  const selectUi = computed(() => ({
    base: `${buttonWidthClass.value} cursor-default dark:text-content-level-300 flex-shrink-0 justify-center opacity-50 p-1 rounded-l-md text-content-level-700 text-sm`,
    itemTrailingIcon: 'hidden'
  }))

  return {
    buttonLabel,
    contentProps,
    countryItems,
    erroredFlags,
    isSelectedCountry,
    loading,
    onCountryFlagError,
    onCountrySelect,
    searchInputProps,
    searchQuery,
    selectUi,
    selectedCode,
    selectedCountry,
    showFlagInButton
  }
}
